import { shapes as SHAPE_LIBRARY } from '../data/shapeLibrary';

/**
 * Substitution des pictogrammes reconnus par leur forme HOMOLOGUÉE.
 *
 * À l'import d'un PDF, le serveur extrait les pictogrammes en tracés vectoriels et,
 * quand il les reconnaît, joint l'identifiant de la forme homologuée correspondante
 * (`pictoId`), la boîte à lui donner (`pictoRect`) et le marquage des tracés qu'elle
 * remplace (`pictoCovered`). C'est ici qu'on opère l'échange.
 *
 * POURQUOI SUBSTITUER PLUTÔT QUE GARDER L'EXTRACTION. Le tracé extrait est fidèle en
 * forme mais pas en encre : un noir K seul devient un noir quatre couleurs après
 * extraction puis reconversion, ce qui se voit au tirage sur un losange CLP. La forme
 * de la bibliothèque est celle qui a été validée, et elle est réutilisable — l'opérateur
 * retrouve le même objet que s'il l'avait posé lui-même.
 *
 * DEUX PRÉCAUTIONS. On ne remplace que les tracés MARQUÉS par le serveur : le
 * regroupement peut embarquer un voisin (sur un fichier réel, un cercle blanc qui
 * dépasse du losange), et le supprimer le ferait disparaître du document. Et la boîte
 * vient du serveur, pas du groupe : celle du groupe étirerait la forme.
 */
const PAR_ID = new Map(SHAPE_LIBRARY.map((s) => [s.id, s]));

export function substituerPictos(elements) {
    if (!Array.isArray(elements) || !elements.length) return elements;

    const groupes = elements.filter(
        (el) => el?.type === 'group' && el.pictoId && el.pictoRect && PAR_ID.has(el.pictoId));
    if (!groupes.length) return elements;

    const remplaces = new Set();
    const ajouts = new Map();          // id du groupe → élément de remplacement

    for (const g of groupes) {
        const forme = PAR_ID.get(g.pictoId);
        const couverts = elements.filter((el) => el.parentId === g.id && el.pictoCovered);
        if (!couverts.length) continue;
        couverts.forEach((el) => remplaces.add(el));
        const multi = !!(forme.multiColor && forme.svgContent);
        ajouts.set(g.id, {
            id: `${g.id}-picto`,
            parentId: g.id,
            type: 'shape',
            shapeType: 'svg',
            svgPath: multi ? null : forme.path,
            svgContent: multi ? forme.svgContent : null,
            svgViewBox: forme.viewBox,
            svgFill: forme.fill !== false,
            color: multi ? null : (couverts[0].color || '#000000'),
            x: g.pictoRect.x,
            y: g.pictoRect.y,
            width: g.pictoRect.width,
            height: g.pictoRect.height,
            rotation: 0,
            opacity: 100,
            name: forme.name,
            pictoId: g.pictoId,
        });
    }
    if (!ajouts.size) return elements;

    // On reconstruit la liste en respectant l'ordre : la forme homologuée prend la
    // place du PREMIER tracé qu'elle remplace, pour conserver l'empilement.
    const sortie = [];
    const posees = new Set();
    for (const el of elements) {
        if (remplaces.has(el)) {
            const neuf = ajouts.get(el.parentId);
            if (neuf && !posees.has(el.parentId)) {
                sortie.push(neuf);
                posees.add(el.parentId);
            }
            continue;                  // le tracé extrait disparaît
        }
        sortie.push(el);
    }

    // Un groupe qui ne contient plus que la forme substituée n'apporte rien : on
    // l'aplatit, le calque portant déjà le nom réglementaire.
    const compte = new Map();
    for (const el of sortie) {
        if (el.parentId) compte.set(el.parentId, (compte.get(el.parentId) || 0) + 1);
    }
    const aPlatir = new Set();
    for (const g of groupes) {
        if (compte.get(g.id) === 1 && ajouts.has(g.id)) aPlatir.add(g.id);
    }
    if (!aPlatir.size) return sortie;
    return sortie
        .filter((el) => !(el.type === 'group' && aPlatir.has(el.id)))
        .map((el) => (aPlatir.has(el.parentId)
            ? { ...el, parentId: groupes.find((g) => g.id === el.parentId)?.parentId || null }
            : el));
}
